/**
 * 
 * @copyright     (c) 2025 Gist Applications Inc.
 * @author        Greg Olive greg@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/Proxy/pages/GiftCardDetail/DigitalDetail.js
 * Created:       Tue Jan 07 2025
 * Modified By:   Greg Olive
 * Last Modified: Tue Apr 22 2025
 */

import React, {useState, useEffect, useLayoutEffect, useCallback, useMemo} from "react";
import { useParams } from 'react-router';
import { formattedMoney, encodeBase64, decodeBase64, dispatchSdKEvent, sdkFunctions, setSdkData } from "../../../_utils";
import {GiftCardImage, TextMessage, VideoMessage, AudioMessage, Buttons, Code } from "./_sections";
import QRCode from "react-qr-code";
import { TranslationsContext } from "@proxy/_contexts";
import {Button, RadioButtons} from "@proxy/_components";
import EmailPreview from "@proxy/_components/EmailPreview";

const LandingPreviewSection = ({
  settings,
  giftCard,
  giftCardData,
  translations,
  videos,
  videoError,
  videoLoading,
  additionalCardWrapClasses,
  moneyFormat,
  preview,
  shouldShow,
}) => {

  const balancePageUrl = settings && settings.shop && settings.shop.domain ? "https://" + settings.shop.domain + "/a/gc/gift-card/balance/" : "";

  const cardMarkup = (
    <div className="gc__container" data-section="greeting-card">
      {giftCard.message_from && giftCard.message_from !== "" && (
        <div className="gc__grid-item gc__message-from-text">
          <h2 className="gc__sub_heading">{ translations.landing.heading.replace("{senders_name}", giftCard.message_from)}</h2>
        </div>
      )}
      <div
      className={`gc__grid-item ${additionalCardWrapClasses.join(' ')}`}
      id="gcpAnimatedCardWrapper"
      style={{
        backgroundImage: `url("${giftCard.selected_card_image}")`,
        backgroundSize: 'cover'
      }}>

        <div id="gcpAnimatedCardBackround"></div>
        <div
        id="gcpAnimatedCard"
        // onClick={()=> toggleShowClass()}
        className="gc__animated-card">

          <div className="gc__animated-card-front">
            <div className="gc__giftcard_image">
              <img
                src={giftCard.selected_card_image}
                alt="Giftcard image"
              />
            </div>
            <GiftCardImage
              settings={settings}
              moneyFormat={(preview) ? moneyFormat : giftCard.money_format}
              giftCard={giftCard}
              preview={preview}
            />
          </div>
          <div
          className="gc__animated-card-inside"
          >
          {
            giftCard.message_text ?
              <TextMessage
                giftCard={giftCard}
                salutation={giftCard.message_salutation}
                messageText={giftCard.message_text}
                recipientName={giftCard.recipient_name}
                messageFrom={giftCard.message_from}
                translation={translations}
              />
              : null
          }
          </div>
        </div>
      </div>
    </div>
  );

  const videoMarkup = useMemo(() => {
    return ((videos && videos.length > 0) ?
      <div className="gc__container" data-section="video-playback">
        <div className="gc__grid-item">
          <VideoMessage
            thumbnailUrl={preview == true ? giftCardData.video_thumbnail_url : giftCard.video_thumbnail_url}
            videos={videos}
            error={videoError}
            loading={videoLoading}
            translations={translations}
          />
        </div>
        <div className="gc__grid-item">
          <h2 className="gc__sub_heading">{ translations.landing.video_heading }</h2>
        </div>
      </div>
    : null);
  }, [giftCard, videos, videoError, videoLoading, translations]);

  // The Gift Card code to print. We must add the shopify-giftcard-v1- prefix to the code
  // in order for the point of sale app to recognize it.
  // remove all spaces and make uppercase for better scanning and readability
  const formattedCode = giftCard.issued_gift_card_code.toUpperCase().replace(/\s/g, '') || "";
  const scannableCode = `shopify-giftcard-v1-${formattedCode}`;

  const instructionsMarkup = (((giftCard.recipient_name !== "" || preview === false) && (settings.digital.appearance.enable_qr_code === "1" || settings.digital.appearance.enable_qr_code === true) ) && (
    <div className="gc__container" data-section="instructions">
      <div id="gcpInstructionsContainer" className="gc__grid-item">
        <div>
          <div className="gc__list_wrap">
            <h2 className="gc__sub_heading">
              {translations.landing.instructions_heading}
            </h2>
            <div className="gc__list">
              <div>{ translations.landing.instruction1 }</div>
              <div>{ translations.landing.instruction2.replace("{shop.name}", settings.shop.name).replace("{shop.domain}", settings.shop.domain) }</div>
              <div>{ translations.landing.instruction3 }</div>
              <div>{ translations.landing.instruction4.replace("{gift_card_amount}", giftCard.amount)}</div>
              <div>{ translations.landing.balance_page} <a href={balancePageUrl}>{balancePageUrl}</a></div>
            </div>
          </div>
        </div>
      </div>

      <div className="gc__grid-item">
        <div>

          <div className="gc__qr-code-wrapper">
            <div>
              <h3>{translations.landing.online} <Code code={giftCard.issued_gift_card_code}/></h3>
            </div>
            <div>
              <QRCode value={preview == true ? settings.shop.domain : `${settings.shop.domain}?discount_code=${giftCard.issued_gift_card_code}`} size={124} title={translations.landing.online}/>
            </div>
          </div>
          { settings.digital.appearance.show_in_store_section === "1" && (
            <div className="gc__qr-code-wrapper">
              <div>
                <h3>{translations.landing.in_store}</h3>
              </div>
              <div>
                <QRCode value={preview == true ? `${settings.shop.domain}?discount_code=preview` : scannableCode} size={124} title={translations.landing.in_store}/>
              </div>
            </div>
          )}

          <div id="gcpStartShoppingButtons">
          <Buttons
            shopUrl={settings.shop.myshopify_domain}
            translation={translations}
            giftCard={giftCard}
          />
          </div>
        </div>
      </div>
    </div>)
  );

  return (shouldShow(settings.digital.appearance.enable_landing_preview, "show_landing_preview") === true || preview === false) &&
  (
    <>
      {cardMarkup}
      {videoMarkup}
      {instructionsMarkup}
    </>
  );
};

export default function DigitalDetail({ currentStep, previewSettings, preview, giftCardData, previewVisible, togglePreview, moneyFormat, isMarketsCurrency }) {
  
  const [giftCard, setGiftCard] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videos, setVideos] = useState([]);
  const [currentPreview, setCurrentPreview] = useState("show_landing_preview");
  const [additionalCardWrapClasses, setAdditionalCardWrapClasses] = useState([]);
  const [translations, setTranslations] = useState({});
  const {token} = useParams();
  const previewClassName = preview === true ? "gc__preview-page" : "page-width site-wrapper wrapper main-content container";
  const videoUuid = preview == true ? giftCardData.video_hash : giftCard.video_hash;
  const [styleVars, setStyleVars] = useState({});

  useEffect(() => {
    if (preview) {
      setGiftCard(giftCardData);
      setSettings(previewSettings);
      setTranslations(previewSettings.translations);
      setLoading(false);
    } else {
      fetch(`${window.Shopify?.routes?.root}a/gc/api/giftcards/${token}`)
      .then(res => res.json())
      .then(res => {
        document.cookie = `discount=${res.giftcard.issued_gift_card_code};path=/;secure;samesite=strict`;
        document.cookie = `discount_code=${res.giftcard.issued_gift_card_code};path=/;secure;samesite=strict`;
        setGiftCard(res.giftcard);
        setSettings(res.settings);
        setTranslations(res.settings.translations);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
      });
    }

  }, []);

  useEffect(() => {

    if (!videoUuid || videoUuid === "") return;

    setVideoLoading(true);

    fetch(`${window.Shopify?.routes?.root}a/gc/api/giftcard/videos/${videoUuid}`)
    .then(res => res.json())
    .then(res => {
      if (res.status == "error") {
        setVideoError(true);
      } else {
        setVideos(res.videos);
      }
      setVideoLoading(false);
    })
    .catch(err => {
      console.warn("Error: ", err);
      setVideoError(true);
      setVideoLoading(false);
    })
    ;

  }, [videoUuid]);

  useEffect(() => {
    if (Object.keys(settings).length !== 0) {
      setStyleVars({
        "--gc__var-brand_color": settings.global.appearance.brand_color,
        "--gc__var-accent_color": settings.global.appearance.accent_color,
        "--gc__var-button_text_color": settings.global.appearance.button_text_color,
      })
    }
  }, [settings]);

  /**
   * Set the correct preview to show by default - only in preview mode.
   */
  useEffect(useCallback(() => {
    if ( preview ) {
      setGiftCard(giftCardData);
    }
  },[giftCardData]), [giftCardData]);

  /**
   * Set the correct preview to show by default
   */
  useEffect(useCallback(() => {

    if (!settings || !Object.keys(settings).length) return;

    if (
      settings.digital.appearance.enable_email_preview === "1" &&
      (settings.digital.appearance.enable_landing_preview === "0")
    ) {
      setCurrentPreview("show_email_preview");
    } else {
      setCurrentPreview("show_landing_preview");
    }

  },[currentPreview]), [settings]);

  /**
   * Make sure the preview updates in the admin
   */
  useEffect(useCallback(() => {
    if ( preview ) {
      setSettings(previewSettings);
      if (
        previewSettings.digital.appearance.enable_email_preview === "1" &&
        (previewSettings.digital.appearance.enable_landing_preview === "0")
      ) {
        setCurrentPreview("show_email_preview");
      } else {
        setCurrentPreview("show_landing_preview");
      }
    }
  }, [settings]), [previewSettings]);

  useEffect(() => {
    setVideoError(false);
  }, [giftCardData?.video_hash, giftCard?.video_hash]);

  const addWrapperClass = useCallback( (className) => {

    const newClasses = [...additionalCardWrapClasses];

    if (!newClasses.includes(className)) {
      newClasses.push(className);
    }
    setAdditionalCardWrapClasses(newClasses);

  }, [additionalCardWrapClasses]);

  const removeWrapperClass = useCallback( (className) => {

    const newClasses = [...additionalCardWrapClasses].filter(existingClass => existingClass !== className);

    setAdditionalCardWrapClasses(newClasses);

  }, [additionalCardWrapClasses]);

  const handleCardPositionChange = () => {

    if (preview === true) {

        removeWrapperClass("gc__gift-card-step-animation-done ");
        setTimeout(() => {
          addWrapperClass("gc__animated-initial-load");
        }, 1000);

    } else {

      setTimeout(() => {
        addWrapperClass("gc__gift-card-step-animation-done ");
      }, 4000);

    }

  }

  /**
   * Allow for the card to be opened and closed on click when not in preview.
   */
  const toggleShowClass = useCallback(() => {

    if (preview === true) {
      return;
    }

    if (additionalCardWrapClasses.includes('gc__gift-card-step-animation-done ')) {
      removeWrapperClass("gc__gift-card-step-animation-done ");
    } else {
      addWrapperClass("gc__gift-card-step-animation-done ");
    }

  }, [additionalCardWrapClasses]);

  useLayoutEffect(() => {

    setSdkData('page', {
      'step': currentStep,
    });

    dispatchSdKEvent('gcp-page-loaded');

    // This is handled by useLayoutEffect below.
    if (currentStep === "greeting_card") {


      if (additionalCardWrapClasses.includes("gc__animated-initial-load")) {

        // After the first load, we don't do the skew in animation, but need to close the card instead
        setAdditionalCardWrapClasses(["gc__animated-initial-load", "gs__close-card-animation"]);

        setTimeout(() => {

          setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-animation-start"]);

          setTimeout(() => {
            setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-step-animation-done"]);
          }, 1000);

        }, 300);

        return;

      } else {

        // This sets the Skew in animation.
        setAdditionalCardWrapClasses([""]);

        // We wait 1 second for skew to finish, then start the next animation
        setTimeout(() => {

          setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-animation-start"]);

          setTimeout(() => {
            setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-step-animation-done"]);
          }, 1000);

        }, 1000);

      }

      return;

    }

    if (currentStep === "gift_card" || currentStep === "gift_message") {

      // Don't chang the animation if it is already complete
      if (additionalCardWrapClasses.includes("gc__gift-card-step-animation-done")) {
        return;
      }

      setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__gift-card-animation-start"]);

      setTimeout(() => {

        setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__gift-card-step-animation-done"]);

      }, 300);

      return;

    }

    if (typeof(currentStep) == 'undefined') {


      // setTimeout(() => {

        // We wait 1 second for skew to finish, then start the next animation
        setTimeout(() => {

          setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-animation-start"]);

          setTimeout(() => {
            setAdditionalCardWrapClasses(["gc__animated-initial-load", "gc__greeting-card-step-animation-done"]);
          }, 1000);

        }, 4000);


      // }, 4000);


    }

  }, [currentStep]);

  useLayoutEffect(() => {}, []);

  var stringToHTML = function (str) {
    const dom = document.createElement('div');
    dom.innerHTML = str;
    return dom;
  };

  const shouldShow = (settingValue, previewStateValue) => {

    if (settingValue !== "1" && settingValue !== true) {
      return false;
    }

    if (currentPreview !== previewStateValue) {
      return false;
    }

    return true;

  };

  const RadioButtonSection = () => {

    const showRadio = (() => {

      if (
        typeof(currentStep) == 'undefined' ||
        settings.digital.appearance.enable_email_preview == false ||
        settings.digital.appearance.enable_landing_preview == false
      ) {
        return false;
      }

      return true;

    })();

    return showRadio && (
      <>
      <RadioButtons
        groupName={"gcp_preview_value"}
        name={"gcp_preview_value"}
        options={[
          {
            value: "show_landing_preview",
            label: translations.purchase.preview_landing_button
          },
          {
            value: "show_email_preview",
            label: translations.purchase.preview_email_button
          }
        ]}
        onChange={setCurrentPreview}
        value={currentPreview}
        classes={"gc__no_column_row_gap gc__preview-radio-container"}
      />
      <br/>
      </>
    );

  }

  const EmailPreviewSection = () => {

    return shouldShow(settings.digital.appearance.enable_email_preview, "show_email_preview") === true && (
      <EmailPreview
      previewSettings={settings}
      emailPreviewData={giftCard}
      dirty={false}
      inDashboard={false}
      moneyFormat={moneyFormat}
      isMarketsCurrency={isMarketsCurrency}
      />
    );

  }


  return loading === false ? (
    <TranslationsContext.Provider
      value={translations.landing}
    >
    {preview === false && <style dangerouslySetInnerHTML={{__html: decodeBase64(settings.global.appearance.custom_css) }} />}
    <div className={previewClassName} style={styleVars} >
      <div id="gcpMobilePreviewActions" className="gc__hidden_desktop">
        <Button
          type={"button"}
          fullWidth={true}
          onClick={togglePreview}
        >
        {previewVisible === true ? translations.purchase.close_preview : translations.purchase.open_preview}
        </Button>
      </div>
      <RadioButtonSection/>
      {(Object.keys(settings).length && Object.keys(giftCard).length) ?
        <LandingPreviewSection
          settings={settings}
          giftCard={giftCard}
          giftCardData={giftCardData}
          translations={translations}
          videos={videos}
          videoError={videoError}
          videoLoading={videoLoading}
          additionalCardWrapClasses={additionalCardWrapClasses}
          moneyFormat={moneyFormat}
          preview={preview}
          shouldShow={shouldShow}
        />
      : null}
      <EmailPreviewSection/>
    </div>
    </TranslationsContext.Provider>
  ) : (
    <></>
  )
};
